import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import api from '../../services/axiosInterceptor.js';
import SnackbarEx from '../Helpers/SnackbarEx.jsx';
import TreeScopeFields, {
    canSubmitScope,
    scopePayload,
} from './TreeScopeFields.jsx';
import './inspections.css';

const STATUS_FILTERS = [
    { value: 'all', label: 'All Statuses' },
    { value: 'published', label: 'Published' },
    { value: 'draft', label: 'Draft' },
    { value: 'archived', label: 'Archived' },
];

const NEW_TREE_DRAFT_KEY = 'broyles.inspection.newTreeDraft';

function formatDate(d) {
    if (!d) return '';
    try {
        return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return '';
    }
}

function scopeLabel(tree) {
    if (tree.scopeType === 'sku') {
        const list = Array.isArray(tree.skus) && tree.skus.length ? tree.skus : [tree.sku];
        const cleaned = list.filter(Boolean);
        if (cleaned.length > 1) return `SKU · ${cleaned.length} SKUs`;
        return `SKU · ${cleaned[0] || '—'}`;
    }
    return tree.category || '—';
}

function readNewTreeDraft() {
    try {
        const raw = localStorage.getItem(NEW_TREE_DRAFT_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function writeNewTreeDraft(draft) {
    try {
        localStorage.setItem(NEW_TREE_DRAFT_KEY, JSON.stringify(draft));
    } catch {
        /* ignore quota */
    }
}

function clearNewTreeDraft() {
    try {
        localStorage.removeItem(NEW_TREE_DRAFT_KEY);
    } catch {
        /* ignore */
    }
}

export default function TreesList() {
    const navigate = useNavigate();
    const draft = useMemo(() => readNewTreeDraft(), []);

    const [trees, setTrees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('all');
    const [category, setCategory] = useState('All');
    const [q, setQ] = useState('');
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [newOpen, setNewOpen] = useState(false);
    const [scopeType, setScopeType] = useState(draft?.scopeType || 'category');
    const [newName, setNewName] = useState(draft?.newName || '');
    const [newCategory, setNewCategory] = useState(draft?.newCategory || null);
    const [newSkus, setNewSkus] = useState(() => {
        if (Array.isArray(draft?.newSkus)) return draft.newSkus;
        if (draft?.newSku) return [draft.newSku];
        return [];
    });
    const [creating, setCreating] = useState(false);
    const [azCategories, setAzCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', color: 'success' });

    // Persist New Tree SKU/category draft across tab/page navigation
    useEffect(() => {
        writeNewTreeDraft({
            scopeType,
            newName,
            newCategory,
            newSku: newSkus[0] || null,
            newSkus,
        });
    }, [scopeType, newName, newCategory, newSkus]);

    const loadCategories = useCallback(async () => {
        setLoadingCategories(true);
        try {
            const { data } = await api.get('/api/broylesllc/inspection-trees/meta/categories');
            setAzCategories(Array.isArray(data) ? data : []);
        } catch {
            setAzCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    const categoryOptions = useMemo(() => {
        const fromApi = Array.isArray(azCategories) ? azCategories : [];
        const fromTrees = trees.map((t) => t.category).filter(Boolean);
        return [...new Set(['All', ...fromApi, ...fromTrees])];
    }, [azCategories, trees]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (status !== 'all') params.set('status', status);
            if (category !== 'All') params.set('category', category);
            if (q.trim()) params.set('q', q.trim());
            const { data } = await api.get(`/api/broylesllc/inspection-trees?${params}`);
            setTrees(Array.isArray(data) ? data : []);
        } catch (err) {
            setSnack({ open: true, message: 'Failed to load trees', color: 'error' });
        } finally {
            setLoading(false);
        }
    }, [status, category, q]);

    useEffect(() => {
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [load]);

    const counts = useMemo(() => {
        const all = trees.length;
        const published = trees.filter((t) => t.status === 'published').length;
        const draftCount = trees.filter((t) => t.status === 'draft').length;
        return { all, published, draft: draftCount };
    }, [trees]);

    const createTree = async () => {
        const selection = { scopeType, category: newCategory, skus: newSkus };
        if (!canSubmitScope(selection)) return;
        const scope = scopePayload(selection);
        setCreating(true);
        try {
            const payload = {
                ...scope,
                name:
                    newName.trim() ||
                    (scope.scopeType === 'sku'
                        ? scope.skus.length > 1
                            ? `Inspection · ${scope.skus.length} SKUs`
                            : `Inspection · ${scope.sku}`
                        : `${scope.category} Return Inspection`),
            };
            const { data } = await api.post('/api/broylesllc/inspection-trees', payload);
            setNewOpen(false);
            setNewName('');
            setNewCategory(null);
            setNewSkus([]);
            clearNewTreeDraft();
            navigate(`/inspections/trees/${data._id}`);
        } catch (err) {
            setSnack({
                open: true,
                message: err?.response?.data?.message || 'Could not create tree',
                color: 'error',
            });
        } finally {
            setCreating(false);
        }
    };

    const duplicateTree = async (tree) => {
        try {
            const { data } = await api.post(
                `/api/broylesllc/inspection-trees/${tree._id}/duplicate`
            );
            setSnack({ open: true, message: 'Tree duplicated as draft', color: 'success' });
            navigate(`/inspections/trees/${data._id}`);
        } catch (err) {
            setSnack({
                open: true,
                message: err?.response?.data?.message || 'Could not duplicate tree',
                color: 'error',
            });
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget?._id) return;
        setDeleting(true);
        try {
            const { data } = await api.delete(`/api/broylesllc/inspection-trees/${deleteTarget._id}`);
            setTrees((prev) => prev.filter((t) => t._id !== deleteTarget._id));
            setDeleteTarget(null);
            const n = data?.deletedRecords ?? 0;
            setSnack({
                open: true,
                message:
                    n > 0
                        ? `Deleted “${deleteTarget.name}” and ${n} inspection record${n === 1 ? '' : 's'}`
                        : `Deleted “${deleteTarget.name}”`,
                color: 'success',
            });
        } catch (err) {
            setSnack({
                open: true,
                message: err?.response?.data?.message || 'Could not delete tree',
                color: 'error',
            });
        } finally {
            setDeleting(false);
        }
    };

    const canCreate = canSubmitScope({
        scopeType,
        category: newCategory,
        skus: newSkus,
    });

    const activeFilterSummary = useMemo(() => {
        const parts = [];
        if (q.trim()) parts.push(`“${q.trim()}”`);
        if (status !== 'all') {
            parts.push(STATUS_FILTERS.find((f) => f.value === status)?.label || status);
        }
        if (category !== 'All') parts.push(category);
        return parts.length ? parts.join(' · ') : 'No filters applied';
    }, [q, status, category]);

    return (
        <div className="insp-page">
            <div className="insp-page-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <AccountTreeOutlinedIcon /> Decision trees
                        </h1>
                        <p className="insp-sub">
                            {counts.all} trees · {counts.published} published · {counts.draft} draft
                        </p>
                    </div>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setNewOpen(true)}
                    >
                        New tree
                    </Button>
                </div>
            </div>

            <div className="insp-body">
                <div className={`insp-filters ${filtersOpen ? 'is-open' : 'is-collapsed'}`}>
                    <button
                        type="button"
                        className="insp-filters-toggle"
                        onClick={() => setFiltersOpen((v) => !v)}
                        aria-expanded={filtersOpen}
                    >
                        <span className="insp-filters-toggle-left">
                            <FilterListRoundedIcon sx={{ fontSize: 18 }} />
                            Filters
                            {!filtersOpen && (
                                <span className="insp-filters-summary">{activeFilterSummary}</span>
                            )}
                        </span>
                        <ExpandMoreRoundedIcon
                            className="insp-filters-chevron"
                            sx={{ fontSize: 22 }}
                        />
                    </button>
                    {filtersOpen && (
                        <div className="insp-filters-body">
                            <TextField
                                size="small"
                                placeholder="Search by name, category, or SKU"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ maxWidth: 420, background: '#fff' }}
                            />
                            <div className="insp-pill-row">
                                {STATUS_FILTERS.map((f) => (
                                    <button
                                        key={f.value}
                                        type="button"
                                        className={`insp-pill ${status === f.value ? 'active' : ''}`}
                                        onClick={() => setStatus(f.value)}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                            <div className="insp-pill-row">
                                {categoryOptions.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        className={`insp-pill ${category === c ? 'active-dark' : ''}`}
                                        onClick={() => setCategory(c)}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {loading ? (
                    <CircularProgress size={28} sx={{ m: 4 }} />
                ) : (
                    <>
                        <div className="insp-meta" style={{ marginBottom: 10 }}>
                            Showing {trees.length}
                        </div>
                        {trees.map((tree) => (
                            <div key={tree._id} className="insp-tree-row">
                                <button
                                    type="button"
                                    className="insp-tree-row-main"
                                    onClick={() => navigate(`/inspections/trees/${tree._id}`)}
                                >
                                    <div>
                                        <div className="title">
                                            {tree.name}
                                            <span className={`insp-badge ${tree.status}`}>{tree.status}</span>
                                        </div>
                                        <div className="insp-meta">
                                            {scopeLabel(tree)} · v{tree.version} · {tree.questionCount ?? 0}{' '}
                                            questions · {tree.resultCount ?? 0} results
                                        </div>
                                    </div>
                                    <div className="insp-meta">{formatDate(tree.updatedAt)}</div>
                                </button>
                                <IconButton
                                    size="small"
                                    aria-label={`Duplicate ${tree.name}`}
                                    title="Duplicate tree"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        duplicateTree(tree);
                                    }}
                                    sx={{ color: '#5C6570', flexShrink: 0 }}
                                >
                                    <ContentCopyOutlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label={`Delete ${tree.name}`}
                                    title="Delete tree"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteTarget(tree);
                                    }}
                                    sx={{ color: '#C13515', flexShrink: 0 }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </div>
                        ))}
                        {trees.length === 0 && (
                            <p className="insp-meta">No trees match these filters.</p>
                        )}
                    </>
                )}
            </div>

            <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New decision tree</DialogTitle>
                <DialogContent>
                    <TreeScopeFields
                        scopeType={scopeType}
                        onScopeTypeChange={setScopeType}
                        category={newCategory}
                        onCategoryChange={setNewCategory}
                        skus={newSkus}
                        onSkusChange={setNewSkus}
                        helperText="Bind this tree to an AZ Category or one or more SKUs. You can change the selection before creating. Drafts are saved if you leave this page."
                    />

                    <TextField
                        fullWidth
                        label="Tree name (optional)"
                        margin="dense"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Defaults from category or SKU"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setNewOpen(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained" disabled={creating || !canCreate} onClick={createTree}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
                <DialogTitle>Delete tree permanently?</DialogTitle>
                <DialogContent>
                    <p style={{ margin: 0 }}>
                        This will permanently delete{' '}
                        <strong>{deleteTarget?.name}</strong>
                        {deleteTarget?.status ? ` (${deleteTarget.status})` : ''} and any inspection
                        records for it. This cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions>
                    <Button disabled={deleting} onClick={() => setDeleteTarget(null)}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        onClick={confirmDelete}
                    >
                        {deleting ? 'Deleting…' : 'Delete permanently'}
                    </Button>
                </DialogActions>
            </Dialog>

            <SnackbarEx
                open={snack.open}
                message={snack.message}
                color={snack.color}
                close={() => setSnack((s) => ({ ...s, open: false }))}
            />
        </div>
    );
}
